// DTO → view-model parsers. Every response goes through here (even though the
// wire is already camelCase) so a future server rename breaks only this file
// and consumers never touch a raw DTO.
import type {
  InitiateSigningDto,
  ListingRequestDetailDto,
  ListingRequestDto,
  ListingRequestEventDto,
  ListingRequestListDto,
  ListingRequestSummaryDto,
  PropertyAgreementDto,
  PropertyAgreementSignatoryDto,
} from './listingRequestContracts'
import type {
  ListingRequest,
  ListingRequestDetail,
  ListingRequestEvent,
  ListingRequestListPage,
  ListingRequestSummary,
  PropertyAgreement,
  PropertyAgreementSignatory,
  SigningInitiation,
} from '../types/listingRequestTypes'

export function parseListingRequest(dto: ListingRequestDto): ListingRequest {
  return {
    id: dto.id,
    requestRefCode: dto.requestRefCode ?? null,
    requestStatus: dto.requestStatus,
    propertyName: dto.propertyName ?? null,
  }
}

export function parseListingRequestSummary(dto: ListingRequestSummaryDto): ListingRequestSummary {
  return {
    id: dto.id,
    requestRefCode: dto.requestRefCode ?? null,
    propertyName: dto.propertyName ?? null,
    buildingName: dto.buildingName ?? null,
    locationArea: dto.locationArea ?? null,
    askingPrice: dto.askingPrice ?? null,
    priceAgreed: dto.priceAgreed ?? null,
    currencyCode: dto.currencyCode,
    requestStatus: dto.requestStatus,
    stage: dto.stage,
    stageStep: dto.stageStep,
    stageTotal: dto.stageTotal,
    stageState: dto.stageState,
    actionRequired: dto.actionRequired,
    submittedAt: dto.submittedAt ?? null,
    createdAt: dto.createdAt,
  }
}

export function parseListingRequestList(dto: ListingRequestListDto): ListingRequestListPage {
  return {
    rows: dto.items.map(parseListingRequestSummary),
    pagination: {
      page: dto.pagination.page,
      limit: dto.pagination.limit,
      totalItems: dto.pagination.totalItems,
      totalPages: dto.pagination.totalPages,
    },
  }
}

export function parseListingRequestDetail(dto: ListingRequestDetailDto): ListingRequestDetail {
  return {
    id: dto.id,
    requestRefCode: dto.requestRefCode ?? null,
    requestStatus: dto.requestStatus,
    stage: dto.stage,
    stageStep: dto.stageStep,
    stageTotal: dto.stageTotal,
    stageState: dto.stageState,
    actionRequired: dto.actionRequired,
    ownershipRole: dto.ownershipRole ?? null,
    propertyType: dto.propertyType ?? null,
    propertyName: dto.propertyName ?? null,
    certificateNumber: dto.certificateNumber ?? null,
    propertyNumber: dto.propertyNumber ?? null,
    buildingName: dto.buildingName ?? null,
    locationArea: dto.locationArea ?? null,
    addressLine1: dto.addressLine1 ?? null,
    city: dto.city ?? null,
    countryId: dto.countryId ?? null,
    plotNumber: dto.plotNumber ?? null,
    municipalityNumber: dto.municipalityNumber ?? null,
    totalAreaSqm: dto.totalAreaSqm ?? null,
    titleDeedFileName: dto.titleDeedFileName ?? null,
    isRented: dto.isRented ?? false,
    rentalAgreementFileName: dto.rentalAgreementFileName ?? null,
    owners: dto.owners ?? null,
    askingPrice: dto.askingPrice ?? null,
    adminProposedPrice: dto.adminProposedPrice ?? null,
    priceAgreed: dto.priceAgreed ?? null,
    priceNegotiationStatus: dto.priceNegotiationStatus,
    valuationMin: dto.valuationMin ?? null,
    valuationMax: dto.valuationMax ?? null,
    currencyCode: dto.currencyCode,
    sellerNotes: dto.sellerNotes ?? null,
    decisionNotes: dto.decisionNotes ?? null,
    rejectionReason: dto.rejectionReason ?? null,
    revisionNotes: dto.revisionNotes ?? null,
    ownersVerifiedAt: dto.ownersVerifiedAt ?? null,
    submittedAt: dto.submittedAt ?? null,
    reviewedAt: dto.reviewedAt ?? null,
    approvedAt: dto.approvedAt ?? null,
    promotedAt: dto.promotedAt ?? null,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

export function parseListingRequestEvent(dto: ListingRequestEventDto): ListingRequestEvent {
  return {
    id: dto.id,
    eventType: dto.eventType,
    actorType: dto.actorType,
    summary: dto.summary ?? null,
    data: dto.data ?? null,
    createdAt: dto.createdAt,
  }
}

export function parseListingRequestTimeline(dtos: ListingRequestEventDto[]): ListingRequestEvent[] {
  return dtos.map(parseListingRequestEvent)
}

function parseSignatory(dto: PropertyAgreementSignatoryDto): PropertyAgreementSignatory {
  return {
    id: dto.id,
    signatoryRole: dto.signatoryRole,
    signerName: dto.signerName,
    signerEmail: dto.signerEmail,
    signingOrder: dto.signingOrder,
    signatureStatus: dto.signatureStatus,
    signedAt: dto.signedAt ?? null,
  }
}

export function parseAgreement(dto: PropertyAgreementDto): PropertyAgreement {
  return {
    id: dto.id,
    agreementType: dto.agreementType,
    status: dto.status,
    attemptNo: dto.attemptNo,
    signatoryCount: dto.signatoryCount,
    signedCount: dto.signedCount,
    allSigned: dto.allSigned,
    unsignedFilePath: dto.unsignedFilePath ?? null,
    signedFilePath: dto.signedFilePath ?? null,
    generatedAt: dto.generatedAt ?? null,
    sentAt: dto.sentAt ?? null,
    signedAt: dto.signedAt ?? null,
    expiresAt: dto.expiresAt ?? null,
    signatories: dto.signatories.map(parseSignatory),
  }
}

export function parseAgreements(dtos: PropertyAgreementDto[]): PropertyAgreement[] {
  return dtos.map(parseAgreement)
}

export function parseSigningInitiation(dto: InitiateSigningDto): SigningInitiation {
  return {
    signUrl: dto.signUrl,
    agreementId: dto.agreementId,
    signatoryId: dto.signatoryId,
  }
}
